import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { Filter, DayRow, NoInformation } from '../../index';

import {
    StyledSessionTableContainer,
    StyledSessionTableHeader,
    StyledSessionTableBody,
} from './style';

import { alertError } from '../../../redux/stateActions';
import { GET_GROUP_MEETS } from '../../../services/provider';

dayjs.extend(objectSupport);

const GroupSession = ({ staffOptions, selectedStaff, setSelectedStaff }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    /*--------------- Get Group Meet ---------------*/
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [groupMeet, setGroupMeet] = useState([]);

    const variables = useMemo(() => {
        const newVariables = {
            hospital: user.staff.hospital.id,
            startDate: startDate,
            endDate: endDate,
        };

        if (selectedStaff !== '') {
            newVariables.staff = selectedStaff;
        }
        return newVariables;
    }, [selectedStaff, startDate, endDate]);

    const { data: getGroupMeetsData, error: getGroupMeetsError } = useQuery(GET_GROUP_MEETS, {
        variables: variables,
        skip: _.isEmpty(user) || !startDate || !endDate,
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        if (getGroupMeetsError) {
            dispatch(alertError(`Cannot get 1on1 data: ${getGroupMeetsError.message}`));
        }
        if (getGroupMeetsData) {
            const newGroupMeets = [];

            getGroupMeetsData.meets.forEach((meet) => {
                const findIndex = newGroupMeets.findIndex((m) => m.date === meet.startDate);

                if (findIndex !== -1) {
                    newGroupMeets[findIndex].meets.push(meet);
                } else {
                    newGroupMeets.push({
                        date: meet.startDate,
                        meets: [meet],
                    });
                }
            });

            setGroupMeet(newGroupMeets);
        }
    }, [getGroupMeetsData, getGroupMeetsError]);

    const handleFilterChange = ({ isToday, selectedMonth, selectedYear }) => {
        if (isToday) {
            setStartDate(dayjs().startOf('date').format('YYYY-MM-DD'));
            setEndDate(dayjs().endOf('date').format('YYYY-MM-DD'));
        } else {
            setStartDate(
                dayjs()
                    .set({ month: selectedMonth, year: selectedYear })
                    .startOf('month')
                    .format('YYYY-MM-DD')
            );
            setEndDate(
                dayjs()
                    .set({ month: selectedMonth, year: selectedYear })
                    .endOf('month')
                    .format('YYYY-MM-DD')
            );
        }
    };

    return (
        <Fragment>
            <StyledSessionTableContainer>
                <StyledSessionTableHeader>
                    <Filter
                        name="All Group Session"
                        onChange={handleFilterChange}
                        staffOptions={staffOptions}
                        selectedStaff={selectedStaff}
                        setSelectedStaff={setSelectedStaff}
                    />
                </StyledSessionTableHeader>
                <StyledSessionTableBody>
                    {!_.isEmpty(groupMeet) ? (
                        groupMeet.map((item) => <DayRow key={item.date} type="group" data={item} />)
                    ) : (
                        <NoInformation />
                    )}
                </StyledSessionTableBody>
            </StyledSessionTableContainer>
        </Fragment>
    );
};

GroupSession.propTypes = {
    staffOptions: PropTypes.arrayOf(PropTypes.object),
    selectedStaff: PropTypes.string.isRequired,
    setSelectedStaff: PropTypes.func.isRequired,
};

export default GroupSession;
